import {useCallback} from 'react'

import {QueryParam, type SortDirection} from '../helpers'
import {RiaPriority, RiaStatus} from '../types'

import {useSearchParamState} from './useSearchParamState'

export type RiskSurveyQueryState = {
  page: number
  pageSize: number
  sortBy?: string
  sortDir?: SortDirection
  plant?: string
  includeArchived: boolean
  reportId?: string
  status: RiaStatus[]
  priority: RiaPriority[]
}

export function useRiskSurveyQueryState() {
  const {
    readInt,
    readString,
    readBool,
    readSortDir,
    readEnumCsv,
    setParam,
    setPage,
    setSort,
    setSearchParams
  } = useSearchParamState()

  const state: RiskSurveyQueryState = {
    page: readInt(QueryParam.Page, 1),
    pageSize: readInt(QueryParam.PageSize, 20),
    sortBy: readString(QueryParam.SortBy),
    sortDir: readSortDir(),
    plant: readString(QueryParam.Plant),
    includeArchived: readBool(QueryParam.IncludeArchived),
    reportId: readString(QueryParam.ReportId),
    status: readEnumCsv(QueryParam.Status, Object.values(RiaStatus)),
    priority: readEnumCsv(QueryParam.Priority, Object.values(RiaPriority))
  }

  const setFilters = useCallback(
    (filters: {
      plant?: string
      includeArchived?: boolean
      status?: RiaStatus[]
      priority?: RiaPriority[]
    }) => {
      if (filters.plant !== undefined) setParam(QueryParam.Plant, filters.plant)
      if (filters.includeArchived !== undefined)
        setParam(QueryParam.IncludeArchived, filters.includeArchived ? 'true' : '')
      if (filters.status !== undefined) setParam(QueryParam.Status, filters.status.join(','))
      if (filters.priority !== undefined) setParam(QueryParam.Priority, filters.priority.join(','))
    },
    [setParam]
  )

  const setPagination = useCallback(
    (page: number, pageSize: number) => setPage(page, pageSize),
    [setPage]
  )

  const setSorting = useCallback(
    (sortBy: string, sortDir: SortDirection) => setSort(sortBy, sortDir),
    [setSort]
  )

  const setReport = useCallback(
    (reportId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set(QueryParam.ReportId, reportId)
        next.delete(QueryParam.Page)
        next.delete(QueryParam.Status)
        next.delete(QueryParam.Priority)
        return next
      })
    },
    [setSearchParams]
  )

  return {
    state,
    setFilters,
    setPagination,
    setSorting,
    setReport,
    setSearchParams
  }
}
